// Panel dostępnych pokoi (70% szerokości) z widokami cen i formularzami

import React from "react";
import { useAccommodation } from "../../AccommodationProvider";
import ViewModeTabs from "./ViewModeTabs";
import RefreshButton from "./RefreshButton";
import AvailabilityTable from "./AvailabilityTable";
import WeekendStrategyBanner from "./WeekendStrategyBanner";
import { AccommodationHelpers } from "../../utils/accommodationHelpers";

const AvailableRoomsPanel: React.FC = () => {
  const {
    availabilityData,
    isLoading,
    isRefreshing,
    viewMode,
    displayOptions,
    accommodationDays,
    lastRefreshed,
    toggleDisplayOption,
    activeWeekendStrategy,
    markup,
    dismissWeekendStrategy,
    applyWeekendStrategy,
  } = useAccommodation();

  // POPRAWKA: Pobierz wszystkie przypisane pokoje ze wszystkich dni
  const allAssignedRooms = React.useMemo(() => {
    const allRooms: any[] = [];

    // Iteruj przez dni noclegowe (bez ostatniego dnia - checkout)
    const accommodationDaysOnly = accommodationDays.slice(0, -1);

    accommodationDaysOnly.forEach((day) => {
      if (day.planItems) {
        day.planItems
          .filter(
            (item: any) => item.categoryOption?.optionType === "accommodation"
          )
          .forEach((item: any) => {
            allRooms.push({
              id: item.id,
              categoryOptionId: item.categoryOption.id,
              categoryOption: {
                id: item.categoryOption.id,
                name: item.categoryOption.name,
                personsPerRoom: item.categoryOption.personsPerRoom || 1,
              },
              quantity: item.quantity || 0,
              unitPrice: parseFloat(item.unitPrice) || 0,
              occupancyType: item.occupancyType || "pairs",
              personsCountPerRoom: item.personsCountPerRoom,
              offerDayId: day.id,
              linkedItemId: item.linkedItemId,
            });
          });
      }
    });

    return allRooms;
  }, [accommodationDays]);

  // Przetwórz dane dostępności na pokoje
  const availableRooms = React.useMemo(() => {
    if (!availabilityData) return {};

    return AccommodationHelpers.getMinAvailabilityAcrossDays(availabilityData);
  }, [availabilityData]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header z tytułem i przyciskiem odświeżania */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Dostępne pokoje</h3>
        <RefreshButton />
      </div>

      {/* Banner strategii weekendowej */}
      {activeWeekendStrategy &&
        (!markup || Number(markup.accommodationMarkupValue) === 0) && (
          <WeekendStrategyBanner
            strategy={activeWeekendStrategy}
            onApply={() => applyWeekendStrategy(activeWeekendStrategy)}
            onDismiss={() => dismissWeekendStrategy(activeWeekendStrategy.type)}
          />
        )}

      {/* Tabs widoków i opcje */}
      <div className="mb-6 space-y-4">
        <ViewModeTabs />

        {/* Opcje wyświetlania */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={displayOptions.showUnavailable}
              onChange={() => toggleDisplayOption("showUnavailable")}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">Pokaż niedostępne</span>
          </label>

          {lastRefreshed && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              {isRefreshing && (
                <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600"></div>
              )}
              Ostatnia aktualizacja: {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Stan ładowania */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-sm text-gray-600">
            Ładowanie dostępności pokoi...
          </span>
        </div>
      )}

      {/* Tabela pokoi */}
      {!isLoading && (
        <div className={isRefreshing ? "opacity-75" : ""}>
          <AvailabilityTable
            availableRooms={availableRooms}
            viewMode={viewMode}
            assignedRooms={allAssignedRooms}
            showUnavailable={displayOptions.showUnavailable}
          />
        </div>
      )}

      {/* Error state */}
      {!isLoading && !availabilityData && (
        <div className="text-center py-8">
          <div className="text-red-400 mb-2">⚠️</div>
          <p className="text-gray-500 mb-4">
            Nie udało się pobrać danych dostępności pokoi
          </p>
          <RefreshButton />
        </div>
      )}
    </div>
  );
};

export default AvailableRoomsPanel;
